
import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import vuetifyToast from 'vuetify-toast';
import './TimetableEditor.scss'
import DateTimePicker from '@/components/DatetimePicker.vue';
import Timetable from '@/components/Timetable/models/Timetable';
import moment from 'moment';
import { Ride } from '@/components/Timetable/models/Ride';
import { TimetableStates } from '@/components/Timetable/models/TimetableStates';
import { RolePermission } from '@/models/Role';
import Routes from '../../../Routes';

@Component({
    template: require("./TimetableEditor.vue.html"),
    components: {
        DateTimePicker
    }
})
export class TimetableEditor extends Vue {
    static EVENT_TIMETABLE_EDITOR_OPEN = "EVENT_TIMETABLE_EDITOR_OPEN";
    static EVENT_TIMETABLE_EDITOR_CLOSE = "EVENT_TIMETABLE_EDITOR_CLOSE";

    show: boolean = false;
    resetTimeout: any = null;
    showEditor: boolean = false;
    editedTimetable: Timetable = new Timetable(null);
    valid: boolean = true;
    saving: boolean = false;
    deleting: boolean = false;
    $confirm: any;

    startDateString: string = null;
    @Watch('startDateString')
    onStartDateStringChanged(val, oldVal) {
        this.editedTimetable.startDateString = val;
    }

    endDateString: string = null;
    @Watch('endDateString')
    onEndDateStringChanged(val, oldVal) {
        this.editedTimetable.endDateString = val;
    }

    @Watch('show')
    onShowChanged(val, oldVal) {
        // console.log("onShowChanged")
        if (val) {
            if (this.resetTimeout){
                clearTimeout(this.resetTimeout);
            }
        } else {
            // Reset to empty after a small delay (after close transition)
            this.resetTimeout = setTimeout(() => { 
                this.startDateString = null;
                this.endDateString = null;
                this.editedTimetable = new Timetable(null);
            }, 500);
        }
        this.showEditor = val;
    }

    get storeTimetables(){
        return this.$store.state.timetable.timetables;
    }

    get timetableIsLocked(){
        return this.editedTimetable.isLocked;
    }

    get timetableRides(): Ride[] {
        return this.$store.state.ride.rides.filter((ride: Ride) => ride.timetableId === this.editedTimetable.id);
    }

    get routeDestinationId(): number {
        return parseInt(this.$store.state.route.params.destinationId, 10);
    }

    get me() {
        return this.$store.state.user.me;
    }

    get userCanDeleteTimetables(): boolean {
        return this.me 
            ? this.me.hasPermissions(this.routeDestinationId, [RolePermission.DeleteTimetables])
            : false;
    }

    get showWeekends(): boolean {
      return this.$store.state.user.showWeekends;
    }

    get minStartDateString(): string {
        const tomorrowMoment = moment().startOf('day').add(1, 'days');
        return tomorrowMoment.format('YYYY-MM-DD');
    }

    get minEndDateString(): string {
        const tomorrowMoment = moment().startOf('day').add(1, 'days');
        const startDateMoment = moment(this.startDateString);
        const laterMoment = startDateMoment.isAfter(tomorrowMoment) 
            ? startDateMoment
            : tomorrowMoment;
        return laterMoment.format('YYYY-MM-DD');
    }

    @Watch('$route', { immediate: true })
    onRouteChanged(val, oldVal) {
      if (val.name === Routes.timetableListCreateNewRoute.name){
        this.openNewTimetableDialog();
      } else if (oldVal && oldVal.name === Routes.timetableListCreateNewRoute.name) {
        this.closeNewTimetableDialog();
      }
    }
 
    closeNewTimetableDialog() {
      this.$root.$emit(TimetableEditor.EVENT_TIMETABLE_EDITOR_CLOSE);
    }

    openNewTimetableDialog(){
        // console.log("onCreateTimeTableClick", "destinationId:", destinationId);
        const destinationId: number = this.routeDestinationId;
        // When Show weekends is on
        // - startDate always next day
        // When Hide weekends is on
        // - startDate monday if current day is fri(5), sat(6), or sun (0 and 7)
        // Both cases
        // - endDate always same as startDate day

        const startDate = this.showWeekends 
            ? moment().add(1, "days").toDate()
            : moment().isoWeekday() === 0 || moment().isoWeekday() >= 5  
            ? moment().add(1, "weeks").startOf('isoWeek').toDate()
            : moment().add(1, "days").toDate();
        
        const newTimetable: Timetable = new Timetable(
            {
            startDate,
            endDate: startDate,
            destinationId,
            creating: true
            }
        )
        this.onEditTimetable(newTimetable);
    }

    created(){
        this.$root.$on(TimetableEditor.EVENT_TIMETABLE_EDITOR_OPEN, this.onEditTimetable)
        this.$root.$on(TimetableEditor.EVENT_TIMETABLE_EDITOR_CLOSE, this.onCloseEditorEvent)
    }

    onCloseEditorEvent(){
        this.show = false;
    }

    onEditTimetable(timetable: Timetable){

        // console.log("TimetableEditor", "onEditTimetable()", "timetable:", timetable);
        this.editedTimetable = Timetable.CopyFrom(timetable);
        this.startDateString = this.editedTimetable.startDateString;
        this.endDateString = this.editedTimetable.endDateString;
        this.show = true;
    }

    async onStartDateStringUpdated(val, oldVal) {
        // console.log("onStartDateStringUpdated", "val:", val, "oldVal", oldVal);
        // console.log("old startDate:", oldVal);
        // console.log("old endDate:", this.endDateString);
        // console.log("new startDate:", val);
        if (!this.startDateString){
            return;
        }
        this.startDateString = val;
        const startMoment = moment(this.startDateString);
        let endMoment = moment(this.endDateString);
        
        if (startMoment.isAfter(endMoment)){
            // new startDate is after old endDate
            // get the previous duration, in days
            const oldStartMoment = moment(oldVal);
            const previousDurationDays = endMoment.diff(oldStartMoment, 'days');
            
            // set the new end moment to the future so that the duration will be the same
            endMoment = startMoment.clone().add(previousDurationDays, "days");
            // console.log(
            //     "new endDate:", 
            //     val, " plus ", 
            //     previousDurationDays, 
            //     "days =", 
            //     endMoment.format('YYYY-MM-DD')
            // );
            if (endMoment.isoWeekday() > 5 && !this.showWeekends){
                // if the new end moment ended up on a weekend, rewing it to the same week's friday
                endMoment.isoWeekday(5);
            }
            // write out the new endDate 
            this.endDateString = endMoment.format('YYYY-MM-DD');
            // focus the end date picker to catch users attention, to verify autoset date
            (this.$refs.endDatePicker as any).$children[0].isActive = true;
        } 
    }

    async onEndDateStringUpdated(e) {
        this.endDateString = e;
    }

    async onDeleteClick(){
        this.deleting = true;
        // TODO: Check that timetable state allows for deletion

        // Confirm deletion
        let deleteConfirmed = false;
        try {
            await this.$confirm(
                { 
                    title: this.$i18n.translate("timetable.delete.confirm.dialog.title", "Deleting timetable"),
                    message: this.$i18n.translate(
                        "timetable.delete.confirm.dialog.message", 
                        "Do you really want to delete the timetable?"
                    ),
                    okButton: this.$i18n.translate("timetable.delete.confirm.dialog.yes", "Yes"), 
                    cancelButton: this.$i18n.translate("timetable.delete.confirm.dialog.no", "No"),
                }
            )
            deleteConfirmed = true;
        } catch (err) {
            // Confirm deletion was answered "No", we are expecting the confirm dialog to throw an error
        }

        // Check if deletion was confirmed
        if (deleteConfirmed){
            // Deletion was confirmed
            try {
                const destinationId = this.editedTimetable.destinationId;
                await this.$store.dispatch('ride/deleteRides', { data: this.timetableRides.map((r: Ride) => r.id) });
                await this.$store.dispatch('timetable/deleteTimetable', { params: { id: this.editedTimetable.id} });
                this.show = false;
                // TODO: If timetable order has been sent in the past, should we notify Movit of deletion?
            } catch (err) {
                vuetifyToast.error(
                    this.$i18n.translate(
                        'timetable.delete.error.generic', 
                        'There was an error deleting the timetable. Please reload the page and try again later.'
                    )
                )
                console.error("Error when saving passenger:", err);
            }
        }
        this.deleting = false;
    }

    async onSubmit() {
        // console.log("onSubmit")
        if ((this.$refs.form as any).validate()) {
            this.saveTimetable();
        }
    }
    
    async saveTimetable() {
        // console.log("saving timetable");
        console.log(this.editedTimetable);
        this.saving = true;
        try {
            if (this.editedTimetable.creating){
                // Make sure we don't have id set to null, but rather undefined
                delete this.editedTimetable.id;
                // Create
                const response = await this.$store.dispatch(
                    'timetable/createTimetable', 
                    { 
                        data: Timetable.toApi(this.editedTimetable)
                    }
                );

                const newTimetableId = response.data.id;
                
                if (this.editedTimetable.createWithAllDestinationPassengers) {
                    // console.log("with passengers");
                    this.$router.push({
                        name: 'Timetable', 
                        params: { 
                            timetableId: newTimetableId.toString()
                        },
                        query: { created: "true" }
                    });
                } else {
                    // console.log("without passengers?");
                    this.$router.push({
                        name: 'Timetable', 
                        params: { 
                            timetableId: newTimetableId.toString()
                        },
                        query: { addpeople: "true" }
                    });
                }
            } else {
                // Update
                
                await this.$store.dispatch(
                    'timetable/updateTimetable', 
                    { 
                        params: { id: this.editedTimetable.id}, 
                        data: Timetable.toApi(this.editedTimetable)
                    }
                );
            }
        } catch (ex) {
            vuetifyToast.error(
                this.$i18n.translate(
                    "timetable.edit.save.error", 
                    "Something went wrong when saving the timetable. Please try again later."
                )
            );
        }
        this.saving = false;
        this.show = false;
    }

    get routeStateNameLowerCase(): string {
      return this.$store.state.route.params.state;
    }

    get isCreateNewTimetableRoute(): boolean {
        return this.$route.name === Routes.timetableListCreateNewRoute.name
    }

    onCloseClick() {
        if (this.isCreateNewTimetableRoute){
            this.$router.push({
            name: Routes.timetableListWithStateRoute.name, 
            params: { 
                state: this.routeStateNameLowerCase
            }
            })
        } else {
            this.show = false;
        }
    }

    beforeDestroy(){
        if (this.resetTimeout){
            clearTimeout(this.resetTimeout);
        }
        this.$root.$off(TimetableEditor.EVENT_TIMETABLE_EDITOR_OPEN, this.onEditTimetable)
    }
}

export default TimetableEditor;
